import { LOAD_CAPACITY, fmt, makeDefaultTables, layoutTablePositions } from "./calc";
import { LOGO_URL } from "../components/Logo";

function dims(panel, rotated) {
  const hMeters = rotated ? panel.width : panel.length;
  const vMeters = rotated ? panel.length : panel.width;
  return { hMeters, vMeters };
}

export function printDaySchedule(date, { panels = [], placements = [], loads = [], tables, projectName = "", yardCols = 1 }) {
  const tbls = tables && tables.length ? tables : makeDefaultTables();
  const panelsById = Object.fromEntries(panels.map((p) => [p.id, p]));

  // status maps
  const tableOf = {};
  placements.forEach((pl) => {
    const t = tbls.find((x) => x.id === pl.table);
    tableOf[pl.panelId] = t ? t.label : pl.table;
  });
  const loadOf = {};
  loads.forEach((l) => (l.panelIds || []).forEach((pid) => (loadOf[pid] = l.label)));

  const totalVol = panels.reduce((s, p) => s + (p.volume || 0), 0);
  const totalMass = panels.reduce((s, p) => s + (p.mass || 0), 0);
  const usedLoads = loads.filter((l) => (l.panelIds || []).length > 0);

  // position tables using their saved x/y (seed from columns if missing), then scale to fit the page
  const positioned = layoutTablePositions(tbls, Math.min(4, Math.max(1, yardCols || 1)));
  const boundsW = Math.max(1, ...positioned.map((t) => t.x + t.w));
  const boundsH = Math.max(1, ...positioned.map((t) => t.y + t.h));
  const SCALE = Math.min(24, 950 / boundsW);

  const tablesHtml = positioned
    .map((t) => {
      const pls = placements.filter((pl) => pl.table === t.id);
      const boxes = pls
        .map((pl) => {
          const panel = panelsById[pl.panelId];
          if (!panel) return "";
          const { hMeters, vMeters } = dims(panel, pl.rotated);
          return `<div style="position:absolute;left:${pl.x * SCALE}px;top:${pl.y * SCALE}px;
            width:${Math.max(hMeters * SCALE, 10)}px;height:${Math.max(vMeters * SCALE, 10)}px;
            background:${panel.color}cc;border:1px solid ${panel.color};overflow:hidden;
            display:flex;align-items:center;justify-content:center;">
            <span style="font-size:8px;font-family:monospace;color:#fff;font-weight:bold;padding:0 2px;">${panel.name}</span>
          </div>`;
        })
        .join("");
      return `<div class="tbl" style="position:absolute;left:${t.x * SCALE}px;top:${t.y * SCALE + 16}px;">
        <div class="tbl-h"><b>${t.label}</b><span>${fmt(t.w)} m × ${fmt(t.h)} m · ${pls.length} panel${pls.length !== 1 ? "s" : ""}</span></div>
        <div class="grid" style="width:${t.w * SCALE}px;height:${t.h * SCALE}px;background-size:${SCALE}px ${SCALE}px;">${boxes}</div>
      </div>`;
    })
    .join("");

  const panelCards = panels
    .slice()
    .sort((a, b) => (a.jobNumber || "~").localeCompare(b.jobNumber || "~") || a.name.localeCompare(b.name))
    .map((p) => {
      const loc = tableOf[p.id]
        ? `<span class="loc on">${tableOf[p.id]}</span>`
        : loadOf[p.id]
        ? `<span class="loc ld">${loadOf[p.id]}</span>`
        : `<span class="loc up">Unplaced</span>`;
      return `<div class="pcard" style="border-left:4px solid ${p.color}">
        <div class="pcard-h">
          <b>${p.name}</b>
          ${p.jobNumber ? `<span class="job">${p.jobNumber}</span>` : ""}
          ${loc}
        </div>
        <div class="pcard-sub">${fmt(p.width)} × ${fmt(p.length)} m · ${fmt(p.thickness, 3)} m thick · ${p.shape}</div>
        <div class="pcard-grid">
          <div><span>Area</span><b>${fmt(p.area)} m²</b></div>
          <div><span>Vol</span><b>${fmt(p.volume, 3)} m³</b></div>
          <div><span>Mass</span><b>${fmt(p.mass / 1000, 2)} t</b></div>
          <div><span>f'c</span><b>${fmt(p.strength28)} MPa</b></div>
        </div>
      </div>`;
    })
    .join("");

  const loadsHtml = usedLoads
    .map((l) => {
      const lp = (l.panelIds || []).map((id) => panelsById[id]).filter(Boolean);
      const v = lp.reduce((s, p) => s + (p.volume || 0), 0);
      const over = v > LOAD_CAPACITY;
      const strengths = [...new Set(lp.map((p) => p.strength28).filter((x) => x != null && x > 0))];
      const mpa = strengths.length === 0 ? "" : strengths.length === 1 ? `${strengths[0]} MPa` : `Mixed (${strengths.join("/")})`;
      const items = lp
        .map((p) => `<li><span class="dot" style="background:${p.color}"></span>${p.name} <em>${p.jobNumber || ""}</em> — ${fmt(p.volume, 2)} m³</li>`)
        .join("");
      return `<div class="load ${over ? "over" : ""}">
        <div class="load-h"><b>${l.label}</b><span class="${over ? "overtxt" : ""}">${fmt(v, 2)} / ${LOAD_CAPACITY} m³</span></div>
        ${mpa ? `<div class="load-mpa ${strengths.length > 1 ? "mixed" : ""}">${mpa}</div>` : ""}
        <ul>${items}</ul>
      </div>`;
    })
    .join("");

  const generated = new Date().toLocaleString();

  const html = `<!doctype html><html><head><meta charset="utf-8"/>
  <title>Day Schedule — ${projectName ? projectName + " — " : ""}${date}</title>
  <style>
    @page { size: A4 landscape; margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color:#111; margin:0; }
    .head { display:flex; align-items:center; gap:16px; border-bottom:3px solid #2560C9; padding-bottom:10px; }
    .head img { height:48px; }
    .head h1 { font-size:19px; margin:0; letter-spacing:1px; }
    .head p { margin:2px 0 0; color:#555; font-size:11px; text-transform:uppercase; letter-spacing:2px; }
    .meta { display:flex; gap:16px; margin:14px 0; flex-wrap:wrap; }
    .card { border:1px solid #ddd; padding:8px 12px; min-width:120px; }
    .card .l { font-size:9px; text-transform:uppercase; letter-spacing:1px; color:#777; }
    .card .v { font-size:17px; font-weight:bold; margin-top:2px; }
    h2 { font-size:13px; text-transform:uppercase; letter-spacing:1.5px; color:#2560C9; border-bottom:1px solid #eee; padding-bottom:4px; margin:18px 0 10px; }
    .tbl { margin-bottom:16px; page-break-inside:avoid; }
    .yard-grid-wrap { display:grid; gap:10px 24px; align-items:start; }
    .tbl-h { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:4px; font-size:12px; }
    .tbl-h span { color:#777; font-size:10px; }
    .grid { position:relative; background-color:#fafafa; border:1px solid #bbb;
      background-image:linear-gradient(#e6e6e6 1px,transparent 1px),linear-gradient(90deg,#e6e6e6 1px,transparent 1px); }
    table { width:100%; border-collapse:collapse; font-size:11px; }
    th, td { border:1px solid #ccc; padding:4px 7px; text-align:left; }
    th { background:#2560C9; color:#fff; font-size:9px; text-transform:uppercase; letter-spacing:.4px; }
    .on { color:#0a7d33; font-weight:bold; } .ld { color:#b9770a; font-weight:bold; } .up { color:#888; }
    .pcards-wrap { display:flex; flex-wrap:wrap; gap:10px; }
    .pcard { border:1px solid #ddd; padding:8px 10px 9px; width:238px; page-break-inside:avoid; }
    .pcard-h { display:flex; align-items:center; gap:6px; font-size:12px; }
    .pcard-h b { font-size:13px; }
    .pcard-h .job { font-size:8px; text-transform:uppercase; letter-spacing:.5px; border:1px solid #2560C9; color:#2560C9; padding:1px 3px; }
    .pcard-h .loc { margin-left:auto; font-size:9px; font-weight:bold; }
    .loc.on { color:#0a7d33; } .loc.ld { color:#b9770a; } .loc.up { color:#999; }
    .pcard-sub { font-size:9px; color:#777; text-transform:uppercase; letter-spacing:.4px; margin:4px 0 6px; }
    .pcard-grid { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:4px; }
    .pcard-grid > div { border:1px solid #eee; padding:3px 4px; }
    .pcard-grid span { display:block; font-size:7px; text-transform:uppercase; letter-spacing:.5px; color:#999; }
    .pcard-grid b { font-size:10px; }
    .loads-wrap { display:flex; flex-wrap:wrap; gap:10px; }
    .load { border:1px solid #ddd; padding:8px 10px; width:230px; page-break-inside:avoid; }
    .load.over { border-color:#d11; }
    .load-h { display:flex; justify-content:space-between; font-size:12px; border-bottom:1px solid #eee; padding-bottom:4px; margin-bottom:5px; }
    .load-mpa { font-size:10px; font-weight:bold; color:#2560C9; text-transform:uppercase; letter-spacing:.5px; margin-bottom:5px; }
    .load-mpa.mixed { color:#b9770a; }
    .overtxt { color:#d11; font-weight:bold; }
    .load ul { list-style:none; margin:0; padding:0; }
    .load li { font-size:10px; padding:2px 0; display:flex; align-items:center; gap:5px; }
    .load li em { color:#2560C9; font-style:normal; font-size:9px; }
    .dot { width:8px; height:8px; display:inline-block; flex:0 0 auto; }
    .foot { margin-top:20px; font-size:9px; color:#999; border-top:1px solid #eee; padding-top:6px; }
  </style></head><body>
  <div class="head">
    <img src="${LOGO_URL}" alt="EAZIKAST"/>
    <div><h1>DAILY YARD SCHEDULE — ${date}</h1><p>${projectName ? projectName + " · " : ""}EAZIKAST — Yard Organizer</p></div>
  </div>
  <div class="meta">
    <div class="card"><div class="l">Panels</div><div class="v">${panels.length}</div></div>
    <div class="card"><div class="l">Total Volume</div><div class="v">${fmt(totalVol, 2)} m³</div></div>
    <div class="card"><div class="l">Total Mass</div><div class="v">${fmt(totalMass / 1000, 2)} t</div></div>
    <div class="card"><div class="l">Loads Used</div><div class="v">${usedLoads.length}</div></div>
    <div class="card"><div class="l">Tables</div><div class="v">${tbls.length}</div></div>
  </div>

  <h2>Yard Map</h2>
  ${tablesHtml ? `<div class="yard-map" style="position:relative;height:${boundsH * SCALE + 26}px;">${tablesHtml}</div>` : '<p style="color:#999;font-size:12px">No tables configured.</p>'}

  <h2>Panel Schedule</h2>
  <div class="pcards-wrap">${panelCards || '<p style="color:#999;font-size:12px">No panels.</p>'}</div>

  <h2>Loads</h2>
  <div class="loads-wrap">${loadsHtml || '<p style="color:#999;font-size:12px">No panels assigned to loads.</p>'}</div>

  <div class="foot">Generated by EAZIKAST Yard Organizer · ${generated}</div>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}
